// src/GameBoard.js
import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { TransformWrapper, TransformComponent, MiniMap } from 'react-zoom-pan-pinch';
import Coordinates from './Coordinates';
import './GameBoard.css';
import { transformCoord } from './utils';

const TILE_TYPE = { PHUTBALL: -1, PHUTBALL_MAN: 1 };
const JUMP_NODE_START_COLOR = [255, 255, 255];
const JUMP_NODE_END_COLOR = [0, 245, 245];
const GRID_LINE_THICKNESS = 3;

const clamp01 = (value) => Math.max(0, Math.min(1, value));
const lerp = (a, b, t) => a + (b - a) * t;
const lerpColor = (start, end, t) => {
  const clamped = clamp01(t);
  const r = Math.round(lerp(start[0], end[0], clamped));
  const g = Math.round(lerp(start[1], end[1], clamped));
  const b = Math.round(lerp(start[2], end[2], clamped));
  return `rgb(${r}, ${g}, ${b})`;
};
const getUsageSymbol = (count) => {
  if (count <= 1) return '·';
  if (count === 2) return ':';
  if (count === 3) return '⋮';
  if (count === 4) return '⁘';
  return '⁙';
};
const posKey = (r, c) => `${r},${c}`;

const MiniMapPreview = ({ preview, boardPadding, cellSize, pieceRadius, viewPerspective, numRows, numCols, boardWidth, boardHeight, topEndzoneColor }) => {
  const [jumpPhase, setJumpPhase] = useState(0);

  useEffect(() => {
    setJumpPhase(0);
    if (!preview || preview.type !== 'jump' || !preview.jumpPath?.length) return;
    const animFrom = typeof preview.animateFrom === 'number' ? preview.animateFrom : 0;
    const animSegments = preview.jumpPath.length - animFrom;
    const totalSteps = animSegments + 2; // +1 for start point, +1 for pause
    const timer = setInterval(() => {
      setJumpPhase(prev => (prev + 1) % totalSteps);
    }, 600);
    return () => clearInterval(timer);
  }, [preview]);

  if (!preview || preview.type === 'none') return null;

  const cx = (c) => boardPadding + (c + 1) * cellSize;
  const cy = (r) => boardPadding + (r + 1) * cellSize;
  const txPos = (pos) => {
    if (!pos) return null;
    const [dispR, dispC] = transformCoord(pos[0], pos[1], viewPerspective, numRows, numCols);
    return { x: cx(dispC), y: cy(dispR) };
  };

  switch (preview.type) {
    case 'highlight-zones':
      return (
        <div className="minimap-preview-zone" style={{
          top: 0, left: 0, width: '100%',
          height: `${2 * cellSize}px`,
          backgroundColor: topEndzoneColor,
        }} />
      );

    case 'cursor-move': {
      const from = txPos(preview.from);
      const to = txPos(preview.to);
      if (!from || !to) return null;
      return (
        <div className="minimap-preview-cursor" style={{
          width: `${cellSize}px`,
          height: `${cellSize}px`,
          '--from-x': `${from.x}px`,
          '--from-y': `${from.y}px`,
          '--to-x': `${to.x}px`,
          '--to-y': `${to.y}px`,
        }} />
      );
    }

    case 'place': {
      const pos = txPos(preview.to);
      if (!pos) return null;
      return (
        <div className="minimap-preview-ghost" style={{
          left: `${pos.x}px`,
          top: `${pos.y}px`,
          width: `${pieceRadius * 2}px`,
          height: `${pieceRadius * 2}px`,
        }} />
      );
    }

    case 'jump': {
      const { jumpPath, ballStart, animateFrom, invalidIndex: previewInvalid } = preview;
      if (!jumpPath?.length || !ballStart) return null;
      const fullPath = [ballStart, ...jumpPath];
      const points = fullPath.map(txPos).filter(Boolean);
      if (points.length < 2) return null;

      // animateFrom: index into jumpPath where animation starts (0-based)
      // points has ballStart prepended, so animateFrom in points space = animateFrom + 1
      const animStartIdx = typeof animateFrom === 'number' ? animateFrom + 1 : 0;
      // invalidIndex: index into jumpPath (0-based) where chain becomes invalid
      // In points space: invalidPointIdx = invalidIndex + 1
      const hasInvalid = typeof previewInvalid === 'number' && previewInvalid >= 0;
      const invalidPointIdx = hasInvalid ? previewInvalid + 1 : -1;

      const lines = [];
      for (let i = 1; i < points.length; i++) {
        const isStatic = i < animStartIdx;
        const isInvalid = hasInvalid && i >= invalidPointIdx;
        lines.push(
          <line key={i} x1={points[i-1].x} y1={points[i-1].y} x2={points[i].x} y2={points[i].y}
            className="minimap-preview-jump-line"
            style={{
              ...(isStatic && { opacity: 0.35, strokeDasharray: '6, 10' }),
              ...(isInvalid && { stroke: 'rgba(255, 0, 90, 0.8)' }),
            }}
          />
        );
      }

      // Landing dot: red if invalid, cyan if valid
      const landingPoint = points[points.length - 1];
      const landingIsInvalid = hasInvalid;

      // Ball animation: only animate over the active segment (skip when invalid)
      let ballPos = null;
      if (!hasInvalid) {
        if (animStartIdx > 0) {
          const animPoints = points.slice(animStartIdx - 1);
          const totalSteps = animPoints.length + 1;
          const phase = jumpPhase % totalSteps;
          const idx = phase < animPoints.length ? phase : null;
          ballPos = idx !== null ? animPoints[idx] : null;
        } else {
          const ballIdx = jumpPhase < points.length ? jumpPhase : null;
          ballPos = ballIdx !== null ? points[ballIdx] : null;
        }
      }

      // Position hint near the animation start point
      const hintPoint = animStartIdx > 0 ? points[animStartIdx - 1] : points[0];

      return (
        <>
          <svg className="minimap-preview-svg" width={boardWidth} height={boardHeight}>
            {lines}
          </svg>
          {landingPoint && (
            <div className={landingIsInvalid ? "minimap-preview-invalid" : "minimap-preview-ghost"} style={{
              left: `${landingPoint.x}px`,
              top: `${landingPoint.y}px`,
              width: `${pieceRadius * 1.4}px`,
              height: `${pieceRadius * 1.4}px`,
            }} />
          )}
          {ballPos && (
            <div className="minimap-preview-ball" style={{
              left: `${ballPos.x}px`,
              top: `${ballPos.y}px`,
              width: `${pieceRadius * 1.6}px`,
              height: `${pieceRadius * 1.6}px`,
            }} />
          )}
          {preview.label && (
            <div className="minimap-preview-hint" style={{
              top: `${hintPoint.y + pieceRadius * 4}px`,
              left: `${hintPoint.x}px`,
            }}>{preview.label}</div>
          )}
        </>
      );
    }

    case 'button-hint': {
      const hintBallPos = preview.ballPos ? txPos(preview.ballPos) : null;
      return (
        <>
          {hintBallPos && (
            <div className="minimap-preview-ball" style={{
              left: `${hintBallPos.x}px`,
              top: `${hintBallPos.y}px`,
              width: `${pieceRadius * 1.6}px`,
              height: `${pieceRadius * 1.6}px`,
            }} />
          )}
          <div className="minimap-preview-hint" style={hintBallPos ? {
            top: `${hintBallPos.y + pieceRadius * 4}px`,
            left: `${hintBallPos.x}px`,
          } : undefined}>
            {preview.label || 'ENTER'}
          </div>
        </>
      );
    }

    default:
      return null;
  }
};

const GameBoard = (props) => {
  const {
    board, phutballPos, onCellClick, onHoverCell, onLeaveBoard,
    coordSystem, viewPerspective: viewPerspectiveProp, liveNodeId,
    currentNodeId = null,
    isPreviewingStagedMove = false,
    cursorPos, hoverPos, stagedMove, jumpPreview, invalidPlacement,
    onControlsReady,
    showSegmentBar = true,
    disableBoardCursor = false,
    tutorialHighlightPos = null,
    tutorialPreview = null,
    onBoardTransformed = null,
    showMinimap = true,
    boardSessionToken = null,
  } = props;

  const scaleRef = useRef(1);
  const twRef = useRef(null);
  const containerRef = useRef(null);
  const containerSizeRef = useRef({ w: 1, h: 1 });
  const boardSizeRef = useRef({ w: 1, h: 1 });
  const contentHeightRef = useRef(1);
  const timelineShiftRef = useRef(0);
  const didAutoFit = useRef(false);
  const fitAndCenterRef = useRef(null);
  const boardElRef = useRef(null);
  const rotateGenRef = useRef(0);
  const pendingPerspectiveRef = useRef(null);
  const prevLiveNodeRef = useRef(liveNodeId);

  // Delayed perspective: rendering stays on old value until animation completes
  const [renderPerspective, setRenderPerspective] = useState(viewPerspectiveProp);
  const viewPerspective = renderPerspective; // all existing rendering code uses this

  // Expand minimap when tutorial preview changes, hold for 5 animation cycles
  const [minimapExpanded, setMinimapExpanded] = useState(false);
  useEffect(() => {
    if (!tutorialPreview || tutorialPreview.type === 'none') {
      setMinimapExpanded(false);
      return;
    }
    setMinimapExpanded(true);
    let cycleDuration;
    switch (tutorialPreview.type) {
      case 'place': cycleDuration = 1500; break;
      case 'jump': cycleDuration = ((tutorialPreview.jumpPath?.length || 1) + 2) * 600; break;
      default: cycleDuration = 2000; break;
    }
    const timer = setTimeout(() => setMinimapExpanded(false), cycleDuration * 5);
    return () => clearTimeout(timer);
  }, [tutorialPreview]);

  // Phase 1: when perspective prop changes, animate (new live move) or flip instantly
  useEffect(() => {
    // Detect whether liveNodeId changed (= a new move was committed).
    // Skip when previous was null (initial load, not a real move).
    const prevLive = prevLiveNodeRef.current;
    prevLiveNodeRef.current = liveNodeId;
    const liveNodeChanged = prevLive != null && liveNodeId !== prevLive;

    // Clear stale rotation if a previous animation was interrupted.
    const el = boardElRef.current;
    if (el) {
      el.style.transition = '';
      el.style.transform = '';
      el.style.pointerEvents = '';
      el.classList.remove('board-rotating');
    }

    if (viewPerspectiveProp === renderPerspective) return;

    // Only animate on new live moves. Everything else (history nav, first render) = instant.
    if (!liveNodeChanged || !el) {
      setRenderPerspective(viewPerspectiveProp);
      return;
    }

    const gen = ++rotateGenRef.current;
    const stale = () => gen !== rotateGenRef.current;

    // Rotate board 0° → 180° — coords on all four sides rotate with it
    // Counter-rotate pieces so lighting/shadows stay fixed; disable pointer events
    el.classList.add('board-rotating');
    el.style.pointerEvents = 'none';
    el.style.transition = 'transform 1.8s ease-in-out';
    el.style.transform = 'rotate(180deg)';

    // After rotation, swap to new perspective (useLayoutEffect cleans up)
    setTimeout(() => {
      if (stale()) return;
      pendingPerspectiveRef.current = gen;
      setRenderPerspective(viewPerspectiveProp);
    }, 1850);

    return () => { rotateGenRef.current++; };
  }, [viewPerspectiveProp, liveNodeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 2: after renderPerspective updates, clear rotation before paint
  useLayoutEffect(() => {
    if (pendingPerspectiveRef.current === null) return;
    pendingPerspectiveRef.current = null;
    const el = boardElRef.current;
    if (!el) return;

    // New perspective content + clear 180° rotation = seamless (no visual jump)
    el.style.transition = 'none';
    el.style.transform = '';
    el.style.pointerEvents = '';
    el.classList.remove('board-rotating');
    void el.offsetHeight;
  }, [renderPerspective]);

  const autoFitOnce = () => {
    if (didAutoFit.current) return;
    // Don't lock in autoFit if container has no real dimensions yet (e.g. off-screen panel)
    const ct = containerRef.current;
    if (!ct || ct.clientWidth < 10 || ct.clientHeight < 10) return;
    didAutoFit.current = true;
    fitAndCenter(0);
  };

  useEffect(() => {
    if (!containerRef.current) return;

    let resizeTimer = null;
    const refit = () => {
      const ct = containerRef.current;
      if (!ct) return;
      const w = ct.clientWidth;
      const h = ct.clientHeight;
      containerSizeRef.current = { w, h };
      if (w >= 10 && h >= 10) {
        didAutoFit.current = true;
        fitAndCenterRef.current?.(150);
      }
    };
    const debouncedRefit = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(refit, 100);
    };

    const ro = new ResizeObserver(() => debouncedRefit());
    ro.observe(containerRef.current);

    // Also listen for window resize directly — the container may not resize
    // if it overflows its parent instead of shrinking.
    window.addEventListener('resize', debouncedRefit);

    // On mobile stacked layouts, the board may start off-screen.
    const io = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !didAutoFit.current) {
        requestAnimationFrame(refit);
      }
    }, { threshold: 0.1 });
    io.observe(containerRef.current);

    return () => { ro.disconnect(); io.disconnect(); window.removeEventListener('resize', debouncedRefit); clearTimeout(resizeTimer); };
  }, []);

  const centerToBoard = (scale, duration = 250) => {
    if (!containerRef.current) return;

    const vw = containerRef.current.clientWidth;
    const vh = containerRef.current.clientHeight;
    const { w: bw, h: bh } = boardSizeRef.current;
    if (!bw || !bh) return;

    // Use full content height (includes segment bar space below board) for scale fitting,
    // but nudge down so the board itself is visually centered
    const fullHeight = contentHeightRef.current || bh;
    const timelineSpace = fullHeight - bh;
    const tx = (vw - bw * scale) / 2;
    const ty = (vh - fullHeight * scale) / 2;

    scaleRef.current = scale;
    twRef.current?.setTransform(tx, ty, scale, duration);
  };

  const fitAndCenter = (duration = 250) => {
    if (!containerRef.current) return;

    const vw = containerRef.current.clientWidth;
    const vh = containerRef.current.clientHeight;
    const { w: bw, h: bh } = boardSizeRef.current;

    const fullHeight = Math.max(contentHeightRef.current || bh, bh);
    const baseMargin = Math.max(cellSize * 0.45, 18);
    const tightMargin = Math.max(cellSize * 0.18, 8);

    const baseW = Math.max(1, vw - baseMargin * 2);
    const baseH = Math.max(1, vh - baseMargin * 2);
    const rawScale = Math.max(0.01, Math.min(baseW / bw, baseH / fullHeight));

    const expandedW = Math.max(1, vw - tightMargin * 2);
    const expandedH = Math.max(1, vh - tightMargin * 2);
    const expandedScale = Math.min(expandedW / bw, expandedH / fullHeight);

    const scale = Math.min(rawScale * 0.93, expandedScale);

    centerToBoard(scale, duration);
  };
  fitAndCenterRef.current = fitAndCenter;

  const centerAtCurrentScale = (duration = 250) => {
    const scale = scaleRef.current || 1;
    centerToBoard(scale, duration);
  };

  // const zoomToCell = (r, c, padCells = 2, duration = 250) => {
  //   const cellSize = 40;
  //   const x = (c + 1) * cellSize - padCells * cellSize;
  //   const y = (r + 1) * cellSize - padCells * cellSize;
  //   const w = cellSize + padCells * 2 * cellSize;
  //   const h = cellSize + padCells * 2 * cellSize;
  //   const { w: vw, h: vh } = containerSizeRef.current;
  //   const margin = 16;
  //   // Reserve more space for the jump segment bar
  //   const barSpace = (timelineOffset + timelineHeight) > 0 ? (timelineOffset + timelineHeight + 20) : 0;
  //   const extraMargin = Math.max(margin, barSpace);
  //   const maxW = Math.max(1, vw - margin * 2);
  //   const maxH = Math.max(1, vh - margin * 2 - extraMargin);
  //   const scale = Math.max(0.01, Math.min(maxW / w, maxH / (h + timelineOffset + timelineHeight)));
  //   const tx = Math.round((vw - w * scale) / 2 - x * scale);
  //   const shift = timelineShiftRef.current || 0;
  //   const ty = Math.round((vh - h * scale) / 2 - (y + shift) * scale);
  //   scaleRef.current = scale;
  //   twRef.current?.setTransform(tx, ty, scale, duration);
  // };

  const hasBoard = Array.isArray(board) && board.length > 0 && Array.isArray(board[0]);
  const numRows = hasBoard ? board.length : 0;
  const numCols = hasBoard ? board[0].length : 0;
  const cellSize = 40;
  const pieceRadius = cellSize * 0.45;
  const boardPadding = cellSize;
  const innerWidth = Math.max(numCols + 1, 0) * cellSize;
  const innerHeight = Math.max(numRows + 1, 0) * cellSize;
  const boardBorder = 4; // must match .intersection-board CSS border-width
  const boardWidth = innerWidth + boardPadding * 2 + boardBorder * 2;
  const boardHeight = innerHeight + boardPadding * 2 + boardBorder * 2;
  const sidelineGap = Math.max(cellSize * 0.3, 12);
  const sidelineInset = Math.max(boardPadding - sidelineGap, Math.max(cellSize * 0.12, 4));

  useLayoutEffect(() => {
    const wrapper = twRef.current?.instance?.wrapperComponent;
    if (!wrapper) return;
    if (didAutoFit.current) return;
    if (!boardSizeRef.current.w || !boardSizeRef.current.h) return;
    // Don't lock in autoFit if wrapper has no real dimensions yet
    if (wrapper.clientWidth < 10 || wrapper.clientHeight < 10) return;

    didAutoFit.current = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        fitAndCenter(0);
      });
    });
  }, [boardWidth, boardHeight]);

  useEffect(() => {
    onControlsReady?.({ fitAndCenter, centerAtCurrentScale });
  }, [onControlsReady]);

  if (!hasBoard) {
    return <div className="board-loading">Waiting for game state...</div>;
  }

  const showJumpTimeline = showSegmentBar && stagedMove?.type === 'jump' && stagedMove.path?.length;
  const timelineSpacing = cellSize;
  // Position segment bar below the board
  const timelineOffset = cellSize * 0.3;
  const timelineHeight = cellSize * 1.2;

  let segmentBarProps = null;
  if (showJumpTimeline) {
    const jumpStart = Array.isArray(stagedMove.startPos) ? stagedMove.startPos : phutballPos;
    if (Array.isArray(jumpStart)) {
      const pathPoints = [jumpStart, ...stagedMove.path];
      const invalidIndex = typeof (jumpPreview?.invalidIndex) === 'number' ? jumpPreview.invalidIndex : -1;

      const trackLength = Math.max((pathPoints.length - 1) * timelineSpacing, 0);
      const containerWidth = trackLength + timelineSpacing;

      segmentBarProps = {
        pathPoints,
        invalidIndex,
        spacing: timelineSpacing,
        boardHeight,
        timelineOffset,
        timelineHeight,
        containerWidth,
      };
    }
  }

  const cellCenterX = (c) => boardPadding + (c+1) * cellSize;
  const cellCenterY = (r) => boardPadding + (r+1) * cellSize;

  // Always reserve space for segment bar below the board
  const contentHeight = boardHeight + timelineOffset + timelineHeight;

  const paddedWidth = boardWidth;
  boardSizeRef.current = { w: boardWidth, h: boardHeight };
  contentHeightRef.current = contentHeight;
  timelineShiftRef.current = 0;

  const centerRow = Math.floor((numRows - 1) / 2);
  const centerCol = Math.floor((numCols - 1) / 2);

  const pieces = [];
  // Build set of consumed piece positions for minimap filtering
  const jumpedSet = new Set();
  const jumpedPieces = jumpPreview?.jumpedPieces ?? [];
  jumpedPieces.forEach(([r, c]) => jumpedSet.add(`${r},${c}`));
  const ballMidJump = stagedMove?.type === 'jump' && stagedMove.path?.length > 0;
  // Ball defers to the staged trace while the user is still building a jump
  // (startPos is set on stagedMove during staging and cleared once the
  // animation begins) so a return-to-current-cell hop stays visible behind
  // the ball as it's being planned. Suppressed during tree-preview hovers
  // — there the ball is parked at the previous hop's destination on the
  // parent's snapshot and should sit ON TOP of any trace-node breadcrumbs
  // rendered at the same cell so the user can see where the ball is.
  const ballAnchoredForStaging = stagedMove?.type === 'jump'
    && stagedMove.path?.length > 0
    && stagedMove.startPos != null
    && !isPreviewingStagedMove;
  const minimapPieces = [];

  // Render stones in the row-major iteration loop; render the ball
  // separately at the end so its position in the DOM tree never shifts
  // when a stone is consumed mid-jump. Without this, browsers would
  // sometimes drop the ball's CSS transition during mid-sequence hops
  // because React had to insertBefore the ball into a new sibling slot,
  // which can cancel in-flight transitions.
  let ballEl = null;
  for (let r = 0; r < numRows; r++) {
    for (let c = 0; c < numCols; c++) {
      const val = board[r][c];
      if (val !== TILE_TYPE.PHUTBALL && val !== TILE_TYPE.PHUTBALL_MAN) continue;
      const [dispR, dispC] = transformCoord(r, c, viewPerspective, numRows, numCols, cellSize);
      const isBall = val === TILE_TYPE.PHUTBALL;
      // Stable token while at live node so the multi-hop animation can
      // glide the ball cell-to-cell on a single DOM element. While the
      // user is scrubbing the history tree, mix in currentNodeId so the
      // ball remounts on every node change (no straight-line glide
      // between unrelated snapshots — it just snaps to each one).
      const isAtLiveNode = liveNodeId != null && currentNodeId != null && liveNodeId === currentNodeId;
      const ballScrubToken = isAtLiveNode || currentNodeId == null ? 'live' : currentNodeId;
      // boardSessionToken changes between puzzle attempts (puzzle seed flips).
      // Folding it into the ball key forces a fresh DOM element on the new
      // puzzle so React skips the CSS transition that otherwise glides the
      // ball from the just-completed jump's landing cell to the new puzzle's
      // ball position. That glide reads as a phantom replay of the prior jump.
      const sessionSuffix = boardSessionToken ? `-${boardSessionToken}` : '';
      const key = isBall
        ? `piece-ball-${viewPerspective}-${ballScrubToken}${sessionSuffix}`
        : `piece-${dispR}-${dispC}${sessionSuffix}`;
      const ballStagingClass = isBall && ballAnchoredForStaging ? ' staging-anchor' : '';
      const el = (
        <div
          key={key}
          className={`piece ${isBall ? 'phutball' : 'man'}${ballStagingClass}`}
          style={{
            left: `${cellCenterX(dispC)}px`,
            top: `${cellCenterY(dispR)}px`,
            width: `${pieceRadius * 2}px`,
            height: `${pieceRadius * 2}px`,
          }}
        />
      );
      if (isBall) {
        ballEl = el;
      } else {
        pieces.push(el);
      }
      // Minimap: skip consumed pieces and ball at original pos (will render at current pos)
      const isConsumed = val === TILE_TYPE.PHUTBALL_MAN && jumpedSet.has(`${r},${c}`);
      const isBallMoving = val === TILE_TYPE.PHUTBALL && ballMidJump;
      if (!isConsumed && !isBallMoving) {
        minimapPieces.push(el);
      }
    }
  }
  if (ballEl) {
    // Always last so the DOM index is stable: stones can be removed
    // anywhere above without shifting the ball's slot.
    pieces.push(ballEl);
  }

  // Cursor + hover indicators are rendered as direct siblings of pieces
  // so they out-stack the ball (z-index 4) — when nested inside
  // .overlay-layer (z-index 3) they were clipped beneath the ball
  // whenever they sat on the same cell.
  let cursorEl = null;
  if (!disableBoardCursor && Array.isArray(cursorPos)) {
    const [cursorDispR, cursorDispC] = transformCoord(cursorPos[0], cursorPos[1], viewPerspective, numRows, numCols);
    cursorEl = (
      <div
        key="cursor"
        className="cursor cursor--floating"
        style={{
          left: `${cellCenterX(cursorDispC)}px`,
          top: `${cellCenterY(cursorDispR)}px`,
          width: `${cellSize}px`,
          height: `${cellSize}px`,
        }}
      />
    );
  }
  let hoverEl = null;
  if (Array.isArray(hoverPos) && (cursorPos?.[0] !== hoverPos[0] || cursorPos?.[1] !== hoverPos[1])) {
    const [hoverDispR, hoverDispC] = transformCoord(hoverPos[0], hoverPos[1], viewPerspective, numRows, numCols);
    hoverEl = (
      <div
        key="hover"
        className="hover-cursor hover-cursor--floating"
        style={{
          left: `${cellCenterX(hoverDispC)}px`,
          top: `${cellCenterY(hoverDispR)}px`,
          width: `${cellSize}px`,
          height: `${cellSize}px`,
        }}
      />
    );
  }

  // Add ball at its current mid-jump position for the minimap
  if (ballMidJump) {
    const currentBallPos = stagedMove.path[stagedMove.path.length - 1];
    const [dispR, dispC] = transformCoord(currentBallPos[0], currentBallPos[1], viewPerspective, numRows, numCols, cellSize);
    minimapPieces.push(
      <div
        key="minimap-ball-staged"
        className="piece phutball"
        style={{
          left: `${cellCenterX(dispC)}px`,
          top: `${cellCenterY(dispR)}px`,
          width: `${pieceRadius * 2}px`,
          height: `${pieceRadius * 2}px`,
        }}
      />
    );
  }

  const toCell = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const scale = scaleRef.current || 1;
    const localX = (x / scale) - boardPadding;
    const localY = (y / scale) - boardPadding;
    let col = Math.round(localX / cellSize) - 1;
    let row = Math.round(localY / cellSize) - 1;
    col = Math.max(0, Math.min(numCols - 1, col));
    row = Math.max(0, Math.min(numRows - 1, row));
    if (viewPerspective === 2) {
        return [numRows - 1 - row, numCols - 1 - col];
    }
    return [row, col];
  };

  const handleBoardClick = (e) => {
    const [row, col] = toCell(e);
    onCellClick?.(row, col);
  };
  const handleMouseMove = (e) => {
    const [row, col] = toCell(e);
    onHoverCell?.(row, col);
  };
  const handleMouseLeave = () => onLeaveBoard?.();

  const topEndzoneColor = viewPerspective === 1 ? 'var(--player2-endzone-color)' : 'var(--player1-endzone-color)';
  const bottomEndzoneColor = viewPerspective === 1 ? 'var(--player1-endzone-color)' : 'var(--player2-endzone-color)';
  const oppositePerspective = viewPerspective === 1 ? 2 : 1;
  const topLabelColor = viewPerspective === 1 ? 'var(--player2-label-color)' : 'var(--player1-label-color)';
  const bottomLabelColor = viewPerspective === 1 ? 'var(--player1-label-color)' : 'var(--player2-label-color)';
  // Compute mirror offsets for right/bottom labels using left-based positioning
  // Mirror from grid edge so labels are the same distance from the grid on both sides
  const coordContainerWidth = cellSize * 0.8;
  const primaryOffset = Math.max(4, boardPadding / 2 - cellSize * 0.5);
  const rightOffset = boardPadding + innerWidth + primaryOffset;
  const bottomOffset = boardPadding + innerHeight + primaryOffset;

  const renderStagingLayer = () => {
    const lines = [];
    const divs = [];
    const numRows = board.length;
    const numCols = board[0].length;
    const previewInfo = jumpPreview ?? {};
    const previewJumped = previewInfo.jumpedPieces ?? [];
    const usageCounts = previewInfo.usageCounts ?? {};
    const invalidIndex = typeof previewInfo.invalidIndex === 'number' ? previewInfo.invalidIndex : -1;

    // Cursor is rendered separately as a sibling of the staging layer
    // so it can sit above the ball (overlay-layer's stacking context
    // would otherwise pin it below pieces). See cursorEl below.

    // Hover cursor is also rendered separately (see hoverEl below) so it
    // can stack above the ball, same reason as the main cursor.

    if (Array.isArray(tutorialHighlightPos)) {
        const [dispR, dispC] = transformCoord(tutorialHighlightPos[0], tutorialHighlightPos[1], viewPerspective, numRows, numCols);
        divs.push(
            <div
              key="tutorial-highlight"
              className="tutorial-highlight"
              style={{
                left: `${cellCenterX(dispC)}px`,
                top: `${cellCenterY(dispR)}px`,
                width: `${cellSize}px`,
                height: `${cellSize}px`,
                transform: 'translate(-50%, -50%)',
              }}
            />
        );
    }

    if (stagedMove.type === 'placement' && stagedMove.path.length > 0) {
      const [r, c] = stagedMove.path[0];
      const [dispR, dispC] = transformCoord(r, c, viewPerspective, numRows, numCols);
      const isInvalid = Array.isArray(invalidPlacement) && invalidPlacement[0] === r && invalidPlacement[1] === c;
      divs.push(
        <div
          key="staged-placement"
          className={`piece staged-piece man${isInvalid ? ' invalid' : ''}`}
          style={{
            left: `${cellCenterX(dispC)}px`,
            top: `${cellCenterY(dispR)}px`,
            width: `${pieceRadius * 2}px`,
            height: `${pieceRadius * 2}px`,
            transform: 'translate(-50%, -50%)',
          }}
        />
      );
    }

    if (stagedMove.type === 'jump' && stagedMove.path.length > 0) {
      const jumpStart = Array.isArray(stagedMove.startPos) ? stagedMove.startPos : phutballPos;
      const pathPoints = Array.isArray(jumpStart) ? [jumpStart, ...stagedMove.path] : [phutballPos, ...stagedMove.path];
      for (let i = 0; i < pathPoints.length - 1; i++) {
        const [r1, c1] = pathPoints[i];
        const [r2, c2] = pathPoints[i + 1];
        const [dispR1, dispC1] = transformCoord(r1, c1, viewPerspective, numRows, numCols);
        const [dispR2, dispC2] = transformCoord(r2, c2, viewPerspective, numRows, numCols);
        const x1 = cellCenterX(dispC1);
        const y1 = cellCenterY(dispR1);
        const x2 = cellCenterX(dispC2);
        const y2 = cellCenterY(dispR2);
        const lineClass = invalidIndex >= 0 && i >= invalidIndex ? 'jump-path invalid' : 'jump-path';
        lines.push(<line key={`path-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} className={lineClass} />);
      }

      pathPoints.forEach(([r, c], idx) => {
        if (typeof r !== 'number' || typeof c !== 'number') return;
        const [dispR, dispC] = transformCoord(r, c, viewPerspective, numRows, numCols);
        const nodeBoardPos = idx === 0 ? pathPoints[0] : stagedMove.path[idx - 1];
        if (!Array.isArray(nodeBoardPos)) return;
        const usageKey = posKey(nodeBoardPos[0], nodeBoardPos[1]);
        const usageCount = usageCounts[usageKey] ?? 1;
        const normalizedIdx = invalidIndex >= 0 ? Math.min(idx, invalidIndex + 1) : idx;
        const t = pathPoints.length <= 1 ? 0 : normalizedIdx / (pathPoints.length - 1);
        const easedT = Math.pow(t, 1.6);
        const nodeInvalid = invalidIndex >= 0 && idx >= invalidIndex + 1;
        const backgroundColor = nodeInvalid
          ? 'var(--jump-node-invalid)'
          : lerpColor(JUMP_NODE_START_COLOR, JUMP_NODE_END_COLOR, easedT);

        divs.push(
          <div
            key={`jump-node-${idx}`}
            className={`jump-node${nodeInvalid ? ' invalid' : ''}`}
            style={{
              left: `${cellCenterX(dispC)}px`,
              top: `${cellCenterY(dispR)}px`,
              width: `${pieceRadius * 2}px`,
              height: `${pieceRadius * 2}px`,
              backgroundColor,
            }}
          >
            <span className="jump-node-label">{getUsageSymbol(usageCount)}</span>
          </div>
        );
      });
    }

    previewJumped.forEach(([r, c], i) => {
        const [dispR, dispC] = transformCoord(r, c, viewPerspective, numRows, numCols);
        divs.push(
        <div key={`jumped-${i}`} className="jumped-piece-ghost" style={{
            left: `${cellCenterX(dispC)}px`,
            top: `${cellCenterY(dispR)}px`,
            width: `${pieceRadius * 2}px`,
            height: `${pieceRadius * 2}px`,
            transform: 'translate(-50%, -50%)'
        }}/>
        );
    });

    return (
        <>
          <svg className="svg-overlay" width={boardWidth} height={boardHeight} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
            {lines}
          </svg>
          <div className="overlay-layer">{divs}</div>
        </>
      );
  };

  // Calculate dynamic minScale based on board size
  // For a standard 21x15 board, 0.35 is fine
  // For larger boards, we need to allow zooming out further
  const standardBoardArea = 21 * 15; // 315
  const currentBoardArea = numRows * numCols;
  const areaRatio = currentBoardArea / standardBoardArea;
  // Scale down minScale proportionally, with a floor of 0.05
  const dynamicMinScale = Math.max(0.05, 0.35 / Math.sqrt(areaRatio));

  return (
    <div className={`board-layout-container${minimapExpanded ? ' minimap-expanded' : ''}`} ref={containerRef} style={{ '--board-aspect': `${boardWidth} / ${boardHeight}` }}>
      <div className="zoom-pan-wrapper">
        <TransformWrapper
          ref={twRef}
          initialScale={1}
          minScale={dynamicMinScale}
          maxScale={3}
          wheel={{ step: 0.1 }}
          limitToBounds={false}
          panning={{ velocityDisabled: true }}
          onInit={(ctx) => {
            scaleRef.current = ctx.state.scale;
            requestAnimationFrame(() => requestAnimationFrame(autoFitOnce));
          }}
          onTransformed={({ state }) => {
            scaleRef.current = state.scale;
            onBoardTransformed?.();
          }}
        >
          {showMinimap && (
          <MiniMap width={150} height={100}>
            <div className="minimap-board" style={{ position: "relative", width: boardWidth, height: boardHeight }}>
              <div className="minimap-endzone" style={{ top: `${boardPadding}px`, left: 0, width: `${boardWidth}px`, height: `${2 * cellSize}px`, backgroundColor: viewPerspective === 1 ? 'rgba(26, 84, 146, 0.55)' : 'rgba(150, 35, 55, 0.6)' }} />
              <div className="minimap-endzone" style={{ top: `${boardPadding + boardHeight - 2 * boardPadding - 2 * cellSize}px`, left: 0, width: `${boardWidth}px`, height: `${2 * cellSize}px`, backgroundColor: viewPerspective === 1 ? 'rgba(150, 35, 55, 0.6)' : 'rgba(26, 84, 146, 0.55)' }} />
              {minimapPieces}
              {Array.isArray(tutorialHighlightPos) && (() => {
                const [dispR, dispC] = transformCoord(tutorialHighlightPos[0], tutorialHighlightPos[1], viewPerspective, numRows, numCols);
                return (
                  <div
                    className="minimap-tutorial-highlight"
                    style={{
                      left: `${cellCenterX(dispC)}px`,
                      top: `${cellCenterY(dispR)}px`,
                      width: `${cellSize}px`,
                      height: `${cellSize}px`,
                    }}
                  />
                );
              })()}
              {tutorialPreview && (
                <MiniMapPreview
                  preview={tutorialPreview}
                  boardPadding={boardPadding}
                  cellSize={cellSize}
                  pieceRadius={pieceRadius}
                  viewPerspective={viewPerspective}
                  numRows={numRows}
                  numCols={numCols}
                  boardWidth={boardWidth}
                  boardHeight={boardHeight}
                  topEndzoneColor={topEndzoneColor}
                />
              )}
            </div>
          </MiniMap>
          )}

          <TransformComponent wrapperClass="zoom-wrapper" contentClass="zoom-content-wrapper">
            <div className="board-content" style={{ position: 'relative', width: `${paddedWidth}px`, height: `${contentHeight}px` }}>
              <div
                ref={boardElRef}
                className="intersection-board"
                onClick={handleBoardClick}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                style={{
                  width: `${boardWidth}px`,
                  height: `${boardHeight}px`,
                  '--cell-size': `${cellSize}px`,
                  '--board-padding': `${boardPadding}px`,
                  '--sideline-inset': `${sidelineInset}px`,
                  '--extra-width': `${4 - boardBorder * 2}px`,
                }}
              >
                <div
                  className="endzone-overlay"
                  style={{
                    top: `${boardPadding}px`,
                    left: `${boardPadding}px`,
                    width: `${innerWidth}px`,
                    height: `${2 * cellSize}px`,
                    backgroundColor: topEndzoneColor,
                    borderTopLeftRadius: `${cellSize * 0.3}px`,
                    borderTopRightRadius: `${cellSize * 0.3}px`,
                  }}
                />
                <div
                  className="endzone-overlay"
                  style={{
                    top: `${boardPadding + innerHeight - 2 * cellSize}px`,
                    left: `${boardPadding}px`,
                    width: `${innerWidth}px`,
                    height: `${2 * cellSize}px`,
                    backgroundColor: bottomEndzoneColor,
                    borderBottomLeftRadius: `${cellSize * 0.3}px`,
                    borderBottomRightRadius: `${cellSize * 0.3}px`,
                  }}
                />
                <Coordinates
                  orientation="cols"
                  side="top"
                  count={numCols}
                  cellSize={cellSize}
                  padding={boardPadding / 2}
                  coordSystem={coordSystem}
                  viewPerspective={viewPerspective}
                  labelColor={topLabelColor}
                />
                <Coordinates
                  orientation="cols"
                  side="bottom"
                  count={numCols}
                  cellSize={cellSize}
                  padding={boardPadding / 2}
                  coordSystem={coordSystem}
                  viewPerspective={oppositePerspective}
                  labelColor={bottomLabelColor}
                  positionOffset={bottomOffset}
                  reversePositions
                />
                <Coordinates
                  orientation="rows"
                  side="left"
                  count={numRows}
                  cellSize={cellSize}
                  padding={boardPadding / 2}
                  coordSystem={coordSystem}
                  viewPerspective={viewPerspective}
                  labelColor={topLabelColor}
                />
                <Coordinates
                  orientation="rows"
                  side="right"
                  count={numRows}
                  cellSize={cellSize}
                  padding={boardPadding / 2}
                  coordSystem={coordSystem}
                  viewPerspective={oppositePerspective}
                  labelColor={bottomLabelColor}
                  positionOffset={rightOffset}
                  reversePositions
                />
                {Array.from({ length: Math.max(numRows, 0) }).map((_, i) => {
                  const y = boardPadding + (i + 1) * cellSize;
                  const isCenterLine = (i === centerRow);
                  return (
                    <div
                      key={`h-${i}`}
                      className={`grid-line horizontal ${isCenterLine ? 'origin-line' : ''}`}
                      style={{
                        top: y - (GRID_LINE_THICKNESS / 2),
                        left: boardPadding,
                        width: innerWidth,
                        height: GRID_LINE_THICKNESS,
                      }}
                    />
                  );
                })}
                {Array.from({ length: Math.max(numCols, 0) }).map((_, i) => {
                  const x = boardPadding + (i + 1) * cellSize;
                  const isCenterLine = (i === centerCol);
                  return (
                    <div
                      key={`v-${i}`}
                      className={`grid-line vertical ${isCenterLine ? 'origin-line' : ''}`}
                      style={{
                        left: x - (GRID_LINE_THICKNESS / 2),
                        top: boardPadding,
                        height: innerHeight,
                        width: GRID_LINE_THICKNESS,
                      }}
                    />
                  );
                })}
                {pieces}
                {renderStagingLayer()}
                {hoverEl}
                {cursorEl}
              </div>
              {showJumpTimeline && segmentBarProps && (
                <SegmentBar
                  pathPoints={segmentBarProps.pathPoints}
                  spacing={segmentBarProps.spacing}
                  containerWidth={segmentBarProps.containerWidth}
                  timelineOffset={segmentBarProps.timelineOffset}
                  timelineHeight={segmentBarProps.timelineHeight}
                  boardHeight={segmentBarProps.boardHeight}
                  invalidIndex={segmentBarProps.invalidIndex}
                />
              )}
            </div>
          </TransformComponent>
        </TransformWrapper>
      </div>
    </div>
  );
};

export default GameBoard;

const SegmentBar = ({
  pathPoints,
  spacing,
  containerWidth,
  timelineOffset,
  timelineHeight,
  boardHeight,
  invalidIndex,
}) => {
  if (!Array.isArray(pathPoints) || pathPoints.length === 0) {
    return null;
  }

  const totalPoints = pathPoints.length;
  const trackLength = Math.max((totalPoints - 1) * spacing, 0);
  const defaultWidth = Math.max(trackLength + spacing, spacing);
  const width = containerWidth ?? defaultWidth;

  return (
    <div
      className="jump-segment-bar"
      aria-label="Jump segments preview"
      style={{
        left: '50%',
        transform: 'translate(-50%, 0)',
        top: `${boardHeight + timelineOffset}px`,
        width: `${width}px`,
        height: `${timelineHeight}px`,
      }}
    >
      <div
        className="segment-track"
        style={{
          left: `${spacing / 2}px`,
          width: `${trackLength}px`,
        }}
      />
      <div className="segment-points">
        {pathPoints.map((_, idx) => {
          const classes = ['segment-point'];
          if (idx === 0) classes.push('start');
          if (idx === totalPoints - 1) classes.push('end');
          if (invalidIndex >= 0 && idx >= invalidIndex + 1) classes.push('invalid');

          const t = totalPoints <= 1 ? 0 : idx / (totalPoints - 1);
          const easedT = Math.pow(t, 1.5);
          const fillColor = lerpColor(JUMP_NODE_START_COLOR, JUMP_NODE_END_COLOR, easedT);

          const style = {
            left: `${spacing / 2 + idx * spacing}px`,
          };

          if (!(invalidIndex >= 0 && idx >= invalidIndex + 1)) {
            style.backgroundColor = fillColor;
            style.borderColor = fillColor;
          }

          return (
            <span
              key={idx}
              className={classes.join(' ')}
              style={style}
            />
          );
        })}
      </div>
    </div>
  );
};